import { Insight } from '../../../components/office';
import { Notice } from '@verlo/shared/components/ui/notice';
import { useEffect, useMemo, useState } from 'react';
import { OptionChip } from '@verlo/shared/components/ui/segmented';
import {
  GitCompare, Trophy, ArrowRightLeft, Sparkles, Loader2, Users, MapPin,
  Tag, Package, Calendar, ArrowUpRight, ArrowDownRight, Minus,
} from 'lucide-react';
import { toast } from 'sonner';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import { reportsIntelApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';

const DIMENSIONS = [
  { key: 'staff',    label: 'Staff',     icon: Users,    helper: 'Server vs server' },
  { key: 'site',     label: 'Site',      icon: MapPin,   helper: 'Location vs location' },
  { key: 'category', label: 'Category',  icon: Tag,      helper: 'Menu category vs category' },
  { key: 'product',  label: 'Product',   icon: Package,  helper: 'Item vs item' },
  { key: 'period',   label: 'Period',    icon: Calendar, helper: 'Time window vs window' },
];

const RANGES = [
  { key: 'today',     label: 'Today' },
  { key: 'wtd',       label: 'Week to date' },
  { key: 'mtd',       label: 'Month to date' },
  { key: 'last_7',    label: 'Last 7 days' },
  { key: 'last_30',   label: 'Last 30 days' },
  { key: 'last_90',   label: 'Last 90 days' },
  { key: 'ytd',       label: 'Year to date' },
];

const fmtVal = (v, format) => {
  if (v == null) return '—';
  if (format === 'gbp') return `£${Math.round(v).toLocaleString('en-GB')}`;
  if (format === 'int') return Math.round(v).toLocaleString('en-GB');
  return String(v);
};
const fmtPct = (v) => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;

const ComparisonPage = () => {
  const [dimension, setDimension] = useState('staff');
  const [entities, setEntities] = useState([]);
  const [left, setLeft] = useState('');
  const [right, setRight] = useState('');
  const [range, setRange] = useState('last_30');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load entities for the current dimension
  useEffect(() => {
    (async () => {
      try {
        const res = await reportsIntelApi.compareEntities(dimension);
        setEntities(res.data.entities);
        // Auto-pick first two distinct entities
        if (res.data.entities.length >= 2) {
          setLeft(res.data.entities[0].value);
          setRight(res.data.entities[1].value);
        } else {
          setLeft(''); setRight('');
        }
        setResult(null);
      } catch {
        toast.error(`Could not load ${dimension} list`);
      }
    })();
  }, [dimension]);

  const runCompare = async () => {
    if (!left || !right) { toast.error('Pick a Left and a Right'); return; }
    if (left === right) { toast.error('Left and Right must differ'); return; }
    setLoading(true);
    try {
      const payload = { dimension, left, right, range };
      const res = await reportsIntelApi.compareV2(payload);
      setResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Compare failed');
    } finally { setLoading(false); }
  };

  const swap = () => {
    const a = left; setLeft(right); setRight(a);
    setResult(null);
  };


  const dimMeta = DIMENSIONS.find((d) => d.key === dimension);

  // Max value across both sides for bar scaling (per metric)
  const maxPerMetric = useMemo(() => {
    if (!result) return {};
    const out = {};
    for (const m of result.metrics) {
      out[m.key] = Math.max(Math.abs(m.left), Math.abs(m.right), 1);
    }
    return out;
  }, [result]);

  return (
    <ReportsLayout>
      <div data-testid="comparison-page">
        {/* Header */}
        <div className="flex min-h-[36px] items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <p className="text-[14px] text-ink-soft max-w-2xl">
              Pit any two staff, sites, categories, products or time windows against each other. Verlo grades all seven metrics and crowns a winner.
            </p>
          </div>
        </div>

        {/* Setup card */}
        <div className="bg-white border border-hairline/80 rounded-lg p-5 mb-5" data-testid="comparison-setup">
          {/* Dimension chips */}
          <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-soft mb-2">Dimension</div>
          <div className="flex flex-wrap gap-2 mb-5" data-testid="comparison-dim-row">
            {DIMENSIONS.map((d) => {
              const active = dimension === d.key;
              return (
                <OptionChip
                  key={d.key}
                  selected={active}
                  onClick={() => setDimension(d.key)}
                  className="h-8 text-[12px]"
                  title={d.helper}
                  data-testid={`comparison-dim-${d.key}`}
                >
                  {d.label}
                </OptionChip>
              );
            })}
            <span className="text-[12.5px] text-ink-soft self-center ml-1">{dimMeta?.helper}</span>
          </div>

          {/* Left / vs / Right */}
          <div className="grid md:grid-cols-[1fr_auto_1fr_auto] gap-3 items-end">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-soft mb-1">Left</div>
              <Select value={left} onValueChange={setLeft}>
                <SelectTrigger className="h-auto min-h-12 py-1.5 text-left [&>span]:line-clamp-none" data-testid="comparison-left-select">
                  <SelectValue placeholder={`Pick ${dimMeta?.label.toLowerCase() || 'entity'}`} />
                </SelectTrigger>
                <SelectContent>
                  {entities.filter((e) => e.value !== right).map((e) => (
                    <SelectItem key={e.value} value={e.value}>
                      {/* Name over role, both left-aligned, in the list and in the box (Back
                          office sweep, Oct 2026: they sat misaligned and squashed in the box). */}
                      <div className="flex flex-col items-start text-left leading-tight">
                        <span>{e.label}</span>
                        {e.sub && <span className="mt-0.5 text-[12.5px] text-ink-faint">{e.sub}</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <button
              onClick={swap}
              disabled={!left || !right}
              className="hidden md:flex h-12 w-10 items-center justify-center rounded-md bg-paper hover:bg-hover-tint text-ink-soft disabled:opacity-40 disabled:cursor-not-allowed mb-0.5"
              title="Swap sides"
              data-testid="comparison-swap"
            >
              <ArrowRightLeft size={14} />
            </button>
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-soft mb-1">Right</div>
              <Select value={right} onValueChange={setRight}>
                <SelectTrigger className="h-auto min-h-12 py-1.5 text-left [&>span]:line-clamp-none" data-testid="comparison-right-select">
                  <SelectValue placeholder={`Pick ${dimMeta?.label.toLowerCase() || 'entity'}`} />
                </SelectTrigger>
                <SelectContent>
                  {entities.filter((e) => e.value !== left).map((e) => (
                    <SelectItem key={e.value} value={e.value}>
                      {/* Name over role, both left-aligned, in the list and in the box (Back
                          office sweep, Oct 2026: they sat misaligned and squashed in the box). */}
                      <div className="flex flex-col items-start text-left leading-tight">
                        <span>{e.label}</span>
                        {e.sub && <span className="mt-0.5 text-[12.5px] text-ink-faint">{e.sub}</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={runCompare}
              disabled={loading || !left || !right || left === right}
              className="h-12 bg-verlo hover:bg-verlo-hover gap-1.5"
              data-testid="comparison-run"
            >
              {loading ? <Loader2 size={13} className="animate-spin" /> : <GitCompare size={13} />}
              Compare
            </Button>
          </div>

          {/* Range selector (hidden for period dimension since left/right ARE the periods) */}
          {dimension !== 'period' && (
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-hairline">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-ink-soft">Window</span>
              <Select value={range} onValueChange={setRange}>
                <SelectTrigger className="h-8 w-[160px] text-[12px]" data-testid="comparison-range">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RANGES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-[12.5px] text-ink-soft">Same window applied to both sides</span>
            </div>
          )}
        </div>

        {/* Results */}
        {result && (
          <div data-testid="comparison-results">
            {/* Both-empty state for period comparison */}
            {(() => {
              const allZero = result.metrics.every((m) => Math.abs(m.left) < 0.01 && Math.abs(m.right) < 0.01);
              if (!allZero) return null;
              return (
                <Notice tone="warn" word="No data" className="mb-5" data-testid="comparison-no-data">
                  Neither <b>{result.left.label}</b> nor <b>{result.right.label}</b> had any trade in the requested window. Try a wider range or a different dimension.
                </Notice>
              );
            })()}

            {/* Winner banner */}
            <WinnerBanner result={result} />

            {/* What ran (promotions plan): a promoted fortnight against an unpromoted one is
                not like for like, so the periods say which offers ran and on how many days. */}
            {result.promotions_ran && (result.promotions_ran.left.length > 0 || result.promotions_ran.right.length > 0) && (
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 text-[12.5px]" data-testid="comparison-promotions-ran">
                {['left', 'right'].map((side) => (
                  <div key={side} className="rounded-lg border border-hairline bg-white px-4 py-2.5">
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-ink-faint">Offers in {result[side].label}</p>
                    {result.promotions_ran[side].length === 0
                      ? <p className="text-ink-faint mt-0.5">none ran</p>
                      : <p className="text-ink-soft mt-0.5">{result.promotions_ran[side].map((p) => `${p.name} (${p.days} of ${p.of} days)`).join(' · ')}</p>}
                  </div>
                ))}
              </div>
            )}

            {/* Metric rows */}
            <div className="bg-white border border-hairline/80 rounded-lg overflow-hidden mt-5" data-testid="comparison-metrics">
              <div className="grid grid-cols-[1fr_auto_auto_1fr_auto] gap-3 px-5 py-2.5 border-b border-hairline text-[11px] uppercase tracking-wider font-semibold text-ink-soft">
                <div className="text-right">{result.left.label}</div>
                <div className="w-[110px] text-center">Metric</div>
                <div className="w-[60px] text-center">Δ</div>
                <div className="text-left">{result.right.label}</div>
                <div className="w-[40px]" />
              </div>
              {result.metrics.map((m) => (
                <MetricRow key={m.key} m={m} max={maxPerMetric[m.key]} />
              ))}
            </div>

            {/* Insight */}
            <div className="mt-5">
              <Insight
                key={`${dimension}:${left}:${right}:${range}`}
                read={() => reportsIntelApi.compareInsight({ dimension, left, right, range })}
                purpose="Who is winning, the metric that opens the biggest gap, and one action for this week."
                testId="compare-insight"
              />
            </div>
          </div>
        )}

        {!result && !loading && (
          <div className="bg-surface-2 border border-hairline border-dashed rounded-lg py-12 text-center" data-testid="comparison-empty">
            <GitCompare size={28} className="mx-auto text-ink-faint mb-3" />
            <p className="text-[14px] font-semibold text-ink-soft">Pick a left and right, then hit Compare</p>
            <p className="text-[12px] text-ink-soft mt-1">Verlo will grade seven metrics and crown an overall winner.</p>
          </div>
        )}
      </div>
    </ReportsLayout>
  );
};

// ---------- Sub-components ----------

const WinnerBanner = ({ result }) => {
  const isTie = result.overall_winner === 'tie';
  if (isTie) {
    return (
      <div className="bg-surface-2 border border-hairline rounded-lg px-5 py-4 flex items-center gap-3" data-testid="comparison-winner-banner">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-hairline text-ink-soft text-[11px] font-semibold uppercase tracking-wider">
          <Minus size={11} /> Tied
        </span>
        <span className="text-[13px] text-ink-soft">
          <b className="text-ink">{result.left.label}</b> and <b className="text-ink">{result.right.label}</b> are level: {result.wins.left} wins each, {result.wins.ties} ties.
        </span>
      </div>
    );
  }
  const winnerSide = result.overall_winner;
  const winnerLabel = winnerSide === 'left' ? result.left.label : result.right.label;
  const loserLabel  = winnerSide === 'left' ? result.right.label : result.left.label;
  const winWins  = winnerSide === 'left' ? result.wins.left : result.wins.right;
  const loseWins = winnerSide === 'left' ? result.wins.right : result.wins.left;
  return (
    <div className="bg-signal-ok-tint border border-signal-ok-line rounded-lg px-5 py-4 flex items-center gap-3 flex-wrap" data-testid="comparison-winner-banner">
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-signal-ok text-white text-[11px] font-semibold uppercase tracking-wider">
        <Trophy size={11} /> Winner
      </span>
      <span className="text-[14px] text-signal-ok">
        <b className="font-semibold">{winnerLabel}</b> wins overall ({winWins}–{loseWins} on metrics) against <b className="font-semibold">{loserLabel}</b>
        {result.wins.ties > 0 && <span className="text-[12px] text-signal-ok ml-1">· {result.wins.ties} tie{result.wins.ties === 1 ? '' : 's'}</span>}
      </span>
    </div>
  );
};

const MetricRow = ({ m, max }) => {
  const leftPct = Math.min(100, (Math.abs(m.left) / max) * 100);
  const rightPct = Math.min(100, (Math.abs(m.right) / max) * 100);
  const leftWon = m.winner === 'left';
  const rightWon = m.winner === 'right';
  return (
    <div className="grid grid-cols-[1fr_auto_auto_1fr_auto] gap-3 items-center px-5 py-3 border-t border-hairline" data-testid={`comparison-metric-${m.key}`}>
      {/* Left bar */}
      <div className="flex items-center gap-3 justify-end">
        <div className="font-mono-display text-[13px] font-semibold text-ink tabular-nums w-[100px] text-right">
          {fmtVal(m.left, m.format)}
        </div>
        <div className="flex-1 max-w-[280px] h-2.5 bg-paper rounded overflow-hidden relative">
          <div
            className={`absolute right-0 top-0 bottom-0 ${leftWon ? 'bg-verlo' : 'bg-ink-faint'}`}
            style={{ width: `${leftPct}%` }}
          />
        </div>
      </div>
      {/* Metric label */}
      <div className="w-[110px] text-center">
        <div className="text-[12px] font-semibold text-ink-soft">{m.label}</div>
      </div>
      {/* Delta */}
      <div className="w-[60px] text-center">
        <div className={`text-[12.5px] font-mono-display font-semibold ${m.winner === 'left' ? 'text-signal-ok' : m.winner === 'right' ? 'text-signal-stop' : 'text-ink-soft'}`}>
          {m.delta_pct == null ? <span className="text-ink-faint">new</span> : (
            <span className="inline-flex items-center gap-0.5">
              {m.winner === 'left' && <ArrowUpRight size={10} />}
              {m.winner === 'right' && <ArrowDownRight size={10} />}
              {fmtPct(m.delta_pct)}
            </span>
          )}
        </div>
      </div>
      {/* Right bar */}
      <div className="flex items-center gap-3">
        <div className="flex-1 max-w-[280px] h-2.5 bg-paper rounded overflow-hidden">
          <div
            className={`h-full ${rightWon ? 'bg-verlo' : 'bg-ink-faint'}`}
            style={{ width: `${rightPct}%` }}
          />
        </div>
        <div className="font-mono-display text-[13px] font-semibold text-ink tabular-nums w-[100px]">
          {fmtVal(m.right, m.format)}
        </div>
      </div>
      {/* Winner badge */}
      <div className="w-[40px] text-center">
        {m.winner === 'left'  && <Trophy size={14} className="text-signal-ok mx-auto" />}
        {m.winner === 'right' && <Trophy size={14} className="text-signal-ok mx-auto rotate-180" />}
        {m.winner === 'tie'   && <Minus size={14} className="text-ink-faint mx-auto" />}
      </div>
    </div>
  );
};

export default ComparisonPage;
